import React, { useEffect, useState } from "react";
import {
  Box,
  Input,
  Text,
  Stack,
  Button,
  Flex,
  Portal,
  Select,
  Table,
  SimpleGrid,
} from "@chakra-ui/react";
import { useDispatch } from "react-redux";
import { addBanking, getUsersList } from "../Redux/Action";
import { getLocalStorageItem } from "../Utils/Helper";
import { createListCollection } from "@chakra-ui/react";
import { setDynamicTitle } from "../Utils/dynamicTitleManager";


const initialCryptoData = {
  cryptoName: "",
  cryptoSymbol: "",
  apiKey: "",
  walletName: "",
  type: "crypto"
};

const initialBankData = {
  accountNumber: "",
  accountName: "",
  bankName: "",
  ifsc: "",
  accountType: "",
  type: "bank"
};

const cryptoOptions = [
  { label: "USDT", value: "USDT", symbol: "usdt", group: "Supported" },
];

const cryptoOptionsCollection = createListCollection({
  items: cryptoOptions,
  getKey: (item) => item.value,
  getGroup: (item) => item.group,
});
const accountTypeOptions = [
  { label: "Savings", value: "savings", group: "Account Types" },
  { label: "Current", value: "current", group: "Account Types" },
];

const accountTypeOptionsCollection = createListCollection({
  items: accountTypeOptions,
  getKey: (item) => item.value,
  getGroup: (item) => item.group,
});
const CustomRadioGroup = ({ options, value, onChange }) => {
  return (
    <Flex gap={4}>
      {options.map((option) => (
        <Box
          key={option.value}
          onClick={() => onChange(option.value)}
          cursor="pointer"
          borderWidth="1px"
          borderColor={value === option.value ? "blue.500" : "gray.300"}
          bg={value === option.value ? "blue.50" : "white"}
          px={4}
          py={2}
          borderRadius="md"
          fontWeight={value === option.value ? "bold" : "normal"}
          _hover={{ borderColor: "blue.300" }}
        >
          <Text>{option.label}</Text>
        </Box>
      ))}
    </Flex>
  );
};

const formatDateTime = (value) => {
  const date = new Date(value);
  const options = {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  };
  return date.toLocaleString("en-GB", options).replace(",", "");
};

export const Banking = () => {
  // Use dynamic title management
  useEffect(() => {
    setDynamicTitle("Banking");
  }, []);

  const dispatch = useDispatch();
  const [users, setUsers] = useState([]);
  const [flatOptions, setFlatOptions] = useState([]);
  const [collection, setCollection] = useState(null);
  const [selectedUserId, setSelectedUserId] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [mode, setMode] = useState("");
  const [cryptoData, setCryptoData] = useState(initialCryptoData);
  const [bankData, setBankData] = useState(initialBankData);
  const [selectedCrypto, setSelectedCrypto] = useState(null);
  const [errors, setErrors] = useState({});

  const excludedFields = [
    "fname",
    "lname",
    "password",
    "role",
    "accountno",
    "balance",
    "token",
    "emailverification",
    "kyc_images",
    "ordernumber",
    "createdby",
    "actype",
    "utype",
    "ref",
    "uid",
  ];

  useEffect(() => {
    const whitelabelData = getLocalStorageItem("whitelabelData");
    const payload = whitelabelData
      ? { whiteLabelID: whitelabelData._id, roleFilter: "whitelabel" }
      : { roleFilter: "whitelabel" };

    dispatch(
      getUsersList({
        payload,
        callback: (data) => {
          if (data?.meta?.code === 200) {
            const userList = data?.data?.users || [];

            setUsers(userList);
            const options = userList.map((user) => ({
              label: user.name || user.email || "Unnamed User",
              value: user.whiteLabelId,
              group: user.role || "Others",
            }));

            setFlatOptions(options);

            const userCollection = createListCollection({
              items: options,
              getKey: (item) => item.value,
              getGroup: (item) => item.group,
            });

            setCollection(userCollection);
          }
        },
      })
    );
  }, []);

  useEffect(() => {
    const user = users.find((u) => u.whiteLabelId === selectedUserId);
    setSelectedUser(user || null);
    setMode("");
    setCryptoData(initialCryptoData);
    setBankData(initialBankData);
    setSelectedCrypto(null);
    setErrors({});
  }, [selectedUserId]);

  const handleCryptoChange = (field, value) => {
    setCryptoData((prev) => ({ ...prev, [field]: value }));
  };

  const handleCryptoSelect = (selected) => {
    setSelectedCrypto(selected || null);
    if (selected) {
      setCryptoData((prev) => ({
        ...prev,
        cryptoName: selected.label,
        cryptoSymbol: selected.symbol,
      }));
    } else {
      setCryptoData((prev) => ({
        ...prev,
        cryptoName: "",
        cryptoSymbol: "",
      }));
    }
  };

  const handleBankChange = (field, value) => {
    setBankData((prev) => ({ ...prev, [field]: value }));
  };

  const handleReset = () => {
    if (mode === "crypto") {
      setCryptoData(initialCryptoData);
      setSelectedCrypto(null);
    } else if (mode === "bank") {
      setBankData(initialBankData);
    }
    setErrors({});
  };

  const handleSubmit = () => {
    let currentErrors = {};

    if (mode === "crypto") {
      Object.entries(cryptoData).forEach(([key, value]) => {
        if (!value) currentErrors[key] = "This field is required.";
      });
    } else if (mode === "bank") {
      Object.entries(bankData).forEach(([key, value]) => {
        if (!value) currentErrors[key] = "This field is required.";
      });
    }

    setErrors(currentErrors);

    if (Object.keys(currentErrors).length === 0) {
      const dataToSubmit = mode === "crypto" ? cryptoData : bankData;
      console.log("Submit:", dataToSubmit, "for user", selectedUserId);
      dataToSubmit.whiteLabelId = selectedUserId
      dispatch(
        addBanking({
          payload: dataToSubmit,
          callback: (data) => {
            if (data.meta.code === 200) {
              handleReset()

            } else {
              console.log("Failed");
            }
          },
        })
      );
    }
  };

  return (
    <Stack spacing={6} width="100%">
      {collection && (
        <Select.Root
          maxWidth={"50%"}
          collection={collection}
          size="sm"
          width="100%"
          onValueChange={(val) => {

            const selectedId = Array.isArray(val?.value) ? val.value[0] : val?.value;
            setSelectedUserId(selectedId);
          }}
        >
          <Select.HiddenSelect />
          <Select.Label>Select Whitelabel</Select.Label>
          <Select.Control>
            <Select.Trigger>
              <Select.ValueText placeholder="Select a user" />
            </Select.Trigger>
            <Select.IndicatorGroup>
              <Select.Indicator />
            </Select.IndicatorGroup>
          </Select.Control>
          <Portal>
            <Select.Positioner>
              <Select.Content>
                {Array.from(
                  flatOptions.reduce((acc, item) => {
                    const group = item.group || "Others";
                    if (!acc.has(group)) acc.set(group, []);
                    acc.get(group).push(item);
                    return acc;
                  }, new Map())
                ).map(([group, items]) => (
                  <Select.ItemGroup key={group}>
                    {items.map((item) => (
                      <Select.Item item={item} key={item.value} value={item.value}>
                        {item.label}
                        <Select.ItemIndicator />
                      </Select.Item>
                    ))}
                  </Select.ItemGroup>
                ))}
              </Select.Content>
            </Select.Positioner>
          </Portal>
        </Select.Root>
      )}

      {selectedUser && (
        <Table.ScrollArea borderWidth="1px" rounded="md" maxHeight="30vh" width="100%">
          <Table.Root variant="outline" stickyHeader>
            <Table.Header>
              <Table.Row>
                {Object.keys(selectedUser)
                  .filter(
                    (key) =>
                      !key.startsWith("_") &&
                      !excludedFields.includes(key.toLowerCase())
                  )
                  .map((key) => (
                    <Table.ColumnHeader key={key}>{key}</Table.ColumnHeader>
                  ))}
              </Table.Row>
            </Table.Header>
            <Table.Body>
              <Table.Row>
                {Object.entries(selectedUser)
                  .filter(
                    ([key]) =>
                      !key.startsWith("_") &&
                      !excludedFields.includes(key.toLowerCase())
                  )
                  .map(([key, value]) => (
                    <Table.Cell key={key}>
                      <Text>
                        {["createdAt", "updatedAt"].includes(key)
                          ? formatDateTime(value)
                          : String(value)}
                      </Text>
                    </Table.Cell>
                  ))}
              </Table.Row>
            </Table.Body>
          </Table.Root>
        </Table.ScrollArea>
      )}

      {selectedUser && (
        <CustomRadioGroup
          options={[
            { label: "Add Crypto Details", value: "crypto" },
            { label: "Add Bank Details", value: "bank" },
          ]}
          value={mode}
          onChange={setMode}
        />
      )}

      {mode && (
        <Box p={5} shadow="md" borderWidth="1px" borderRadius="md">
          <Stack spacing={4}>
            <SimpleGrid margin={3} columns={{ base: 1, md: 2 }} spacing={8}>
              {mode === "crypto" && (
                <>
                  <Box margin={2}>
                    <Text fontWeight="medium" mb={1}>
                      Crypto Name
                    </Text>
                    <Select.Root
                      collection={cryptoOptionsCollection}
                      onValueChange={(val) => {
                        const selected = val?.items?.[0] || null;
                        handleCryptoSelect(selected);
                      }}
                      value={selectedCrypto ? [selectedCrypto.value] : []}
                    >
                      <Select.HiddenSelect />
                      <Select.Control>
                        <Select.Trigger>
                          <Select.ValueText placeholder="Select Crypto" />
                        </Select.Trigger>
                        <Select.IndicatorGroup>
                          <Select.Indicator />
                        </Select.IndicatorGroup>
                      </Select.Control>
                      <Portal>
                        <Select.Positioner>
                          <Select.Content>
                            {cryptoOptions.map((crypto) => (
                              <Select.Item
                                key={crypto.value}
                                value={crypto.value}
                                item={crypto}
                              >
                                {crypto.label}
                                <Select.ItemIndicator />
                              </Select.Item>
                            ))}
                          </Select.Content>
                        </Select.Positioner>
                      </Portal>
                    </Select.Root>
                    {errors.cryptoName && (
                      <Text fontSize="sm" color="red.500" mt={1}>
                        {errors.cryptoName}
                      </Text>
                    )}
                  </Box>

                  <Box margin={2}>
                    <Text fontWeight="medium" mb={1}>
                      Crypto Symbol
                    </Text>
                    <Input
                      placeholder="Crypto Symbol"
                      value={cryptoData.cryptoSymbol}
                      readOnly
                    />
                    {errors.cryptoSymbol && (
                      <Text fontSize="sm" color="red.500" mt={1}>
                        {errors.cryptoSymbol}
                      </Text>
                    )}
                  </Box>

                  <Box margin={2}>
                    <Text fontWeight="medium" mb={1}>
                      API Address Key
                    </Text>
                    <Input
                      placeholder="API Address Key"
                      value={cryptoData.apiKey}
                      onChange={(e) => handleCryptoChange("apiKey", e.target.value)}
                    />
                    {errors.apiKey && (
                      <Text fontSize="sm" color="red.500" mt={1}>
                        {errors.apiKey}
                      </Text>
                    )}
                  </Box>

                  <Box margin={2}>
                    <Text fontWeight="medium" mb={1}>
                      Crypto Wallet Name
                    </Text>
                    <Input
                      placeholder="Crypto Wallet Name"
                      value={cryptoData.walletName}
                      onChange={(e) =>
                        handleCryptoChange("walletName", e.target.value)
                      }
                    />
                    {errors.walletName && (
                      <Text fontSize="sm" color="red.500" mt={1}>
                        {errors.walletName}
                      </Text>
                    )}
                  </Box>
                </>
              )}

              {mode === "bank" && (
                <>
                  {Object.entries(bankData)
                    .filter(([field]) => field !== "type")
                    .map(([field, value]) => (
                      <Box margin={2} key={field}>
                        <Text fontWeight="medium" mb={1}>
                          {field
                            .replace(/([A-Z])/g, " $1")
                            .replace(/^./, (char) => char.toUpperCase())}
                        </Text>


                        {field === "accountType" ? (
                          <Select.Root
                            collection={accountTypeOptionsCollection}
                            onValueChange={(val) => {
                              const selected = val?.items?.[0] || null;
                              handleBankChange("accountType", selected?.value || "");
                            }}
                            value={bankData.accountType ? [bankData.accountType] : []}
                          >
                            <Select.HiddenSelect />
                            <Select.Control>
                              <Select.Trigger>
                                <Select.ValueText placeholder="Select Account Type" />
                              </Select.Trigger>
                              <Select.IndicatorGroup>
                                <Select.Indicator />
                              </Select.IndicatorGroup>
                            </Select.Control>
                            <Portal>
                              <Select.Positioner>
                                <Select.Content>
                                  {accountTypeOptions.map((option) => (
                                    <Select.Item
                                      key={option.value}
                                      value={option.value}
                                      item={option}
                                    >
                                      {option.label}
                                      <Select.ItemIndicator />
                                    </Select.Item>
                                  ))}
                                </Select.Content>
                              </Select.Positioner>
                            </Portal>
                          </Select.Root>
                        ) : (
                          <Input
                            placeholder={field.replace(/([A-Z])/g, " $1")}
                            type={field === "accountNumber" ? "number" : "text"}
                            value={value}
                            onChange={(e) => {
                              const inputValue =
                                field === "ifsc" ? e.target.value.toUpperCase() : e.target.value;
                              handleBankChange(field, inputValue);
                            }}
                          />
                        )}

                        {/* Error display */}
                        {errors[field] && (
                          <Text fontSize="sm" color="red.500" mt={1}>
                            {errors[field]}
                          </Text>
                        )}
                      </Box>
                    ))}
                </>
              )}

            </SimpleGrid>

            <Flex gap={4} justify={{ base: "flex-start", md: "flex-end" }} flexWrap="wrap">
              <Button colorScheme="blue" onClick={handleSubmit}>
                Submit
              </Button>
              <Button onClick={handleReset}>Reset</Button>
            </Flex>
          </Stack>
        </Box>
      )}
    </Stack>
  );
};